/**
 * FERP Portal - Floor Plan URL Hook
 *
 * DRR-179: Resolves a floor plan document ID to a signed S3 URL via the
 * requestDocumentDownloadUrl mutation. Local blob URLs are lost on refresh;
 * this hook provides a persistent, refreshable URL.
 *
 * The signed URL expires in 60 minutes. The hook auto-refreshes at 50 minutes.
 *
 * Usage:
 * ```tsx
 * const { url, loading } = useFloorPlanUrl(documentId);
 * <SpatialRenderer floorPlanUrl={url} ... />
 * ```
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { gql } from '@apollo/client/core';
import { useMutation } from '@apollo/client/react';
import { useTenantId } from '@/auth';

const REQUEST_DOWNLOAD_URL = gql`
  mutation RequestFloorPlanDownloadUrl($input: RequestDocumentDownloadUrlInput!) {
    requestDocumentDownloadUrl(input: $input) {
      downloadUrl
      documentName
      mimeType
      expiresInSeconds
    }
  }
`;

interface DownloadUrlData {
  requestDocumentDownloadUrl: {
    downloadUrl: string;
    documentName: string;
    mimeType: string;
    expiresInSeconds: number;
  };
}

interface UseFloorPlanUrlReturn {
  /** Signed S3 URL for the floor plan image (null if not yet resolved) */
  url: string | null;
  /** Whether the URL is being fetched */
  loading: boolean;
  /** Error message if fetch failed */
  error: string | null;
  /** Force refresh the signed URL */
  refresh: () => void;
}

/** Auto-refresh margin: refresh 10 minutes before expiry */
const REFRESH_MARGIN_MS = 10 * 60 * 1000;

export function useFloorPlanUrl(documentId: string | null): UseFloorPlanUrlReturn {
  const tenantId = useTenantId();
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [requestUrl, { loading }] = useMutation<DownloadUrlData>(REQUEST_DOWNLOAD_URL);

  const fetchUrl = useCallback(async () => {
    if (!documentId || !tenantId) return;

    try {
      setError(null);
      const result = await requestUrl({
        variables: { input: { documentId, tenantId } },
      });

      const data = result.data?.requestDocumentDownloadUrl;
      if (data?.downloadUrl) {
        setUrl(data.downloadUrl);

        // Schedule auto-refresh before expiry
        const expiresMs = (data.expiresInSeconds ?? 3600) * 1000;
        const refreshMs = Math.max(expiresMs - REFRESH_MARGIN_MS, 60_000);

        if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = setTimeout(fetchUrl, refreshMs);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load floor plan';
      setError(msg);
    }
  }, [documentId, tenantId, requestUrl]);

  // Fetch on mount / when documentId changes
  useEffect(() => {
    if (documentId) {
      fetchUrl();
    } else {
      setUrl(null);
      setError(null);
    }

    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, [documentId, fetchUrl]);

  return { url, loading, error, refresh: fetchUrl };
}
